import React, { useEffect, useState } from 'react';
import {
  Accordion,
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Link,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { registerDevTool } from '../../registry';
import { client } from '@php-beam/packages/frontend/src/api/client';
import { route } from '@php-beam/packages/frontend/src/hooks/useRouter';
import LeadSearch from '../ResourceComposer/LeadSearch';
import LeadForm from '../ResourceComposer/LeadForm';
import QuoteConfigForm from '../ResourceComposer/QuoteConfigForm';
import LeadRateForm from '../ResourceComposer/LeadRateForm';
import PayrollSettingsForm from '../ResourceComposer/PayrollSettingsForm';
import QuoteForm from '../ResourceComposer/QuoteForm';
import { ResourceDefaults, ActiveLead } from './types';
import Section from './Section';
import ResourceAction from './ResourceAction';
import QuoteSelector from './QuoteSelector';
import SubmissionAction from './SubmissionAction';
import ApprovalAction from './ApprovalAction';
import SubmissionSelector from './SubmissionSelector';

function ResourceComposerV2() {
  const [defaults, setDefaults] = useState<ResourceDefaults | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeLead, setActiveLead] = useState<ActiveLead | null>(null);
  const [activeQuoteId, setActiveQuoteId] = useState<number | null>(null);
  const [activeSubmissionId, setActiveSubmissionId] = useState<number | null>(null);
  const toast = useToast();

  useEffect(() => {
    client
      .get<ResourceDefaults>('/devtools/resources/defaults')
      .then((res) => setDefaults(res.data))
      .catch(() =>
        toast({ title: 'Failed to load defaults', status: 'error' }),
      )
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Text p={4}>Loading...</Text>;
  if (!defaults) return <Text p={4}>Failed to load defaults.</Text>;

  const selectLead = (lead: ActiveLead) => {
    setActiveLead(lead);
    setActiveQuoteId(null);
    setActiveSubmissionId(null);
  };

  return (
    <VStack align="stretch" spacing={0}>
      <Box mb={3}>
        <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={1}>
          Select existing lead
        </Text>
        <LeadSearch
          onSelect={(lead) =>
            selectLead({
              id: lead.id,
              business_name: lead.business_name,
              url: lead.url,
            })
          }
        />
      </Box>

      {activeLead && (
        <>
          <Alert status="success" mb={2} borderRadius="md">
            <AlertIcon />
            <Text fontSize="sm">
              Working with:{' '}
              <Link href={activeLead.url} fontWeight="bold" color="purple.600">
                {activeLead.business_name}
              </Link>{' '}
              (ID: {activeLead.id})
              {activeQuoteId && (
                <Badge ml={2} colorScheme="purple">
                  Quote #{activeQuoteId}
                </Badge>
              )}
              {activeSubmissionId && (
                <Badge ml={1} colorScheme="blue">
                  Submission #{activeSubmissionId}
                </Badge>
              )}
            </Text>
            <Button
              size="xs"
              ml="auto"
              variant="ghost"
              onClick={() => {
                setActiveLead(null);
                setActiveQuoteId(null);
              }}
            >
              Clear
            </Button>
          </Alert>
          <QuoteSelector
            leadId={activeLead.id}
            activeQuoteId={activeQuoteId}
            onSelect={setActiveQuoteId}
          />
          <SubmissionSelector
            leadId={activeLead.id}
            activeSubmissionId={activeSubmissionId}
            onSelect={setActiveSubmissionId}
          />
        </>
      )}

      <Accordion allowMultiple defaultIndex={[0]}>
        <Section
          name="Lead"
          description="Creates via real leads.store endpoint"
        >
          <ResourceAction
            defaults={defaults.lead}
            onSubmit={async (data) => {
              const payload = {
                ...data,
                contact: {
                  name: data.contact_name,
                  title: data.contact_title,
                  details: [
                    ...(data.email
                      ? [{ type: 'email', value: data.email, is_primary: true }]
                      : []),
                    ...(data.phone
                      ? [
                          {
                            type: 'phone_number',
                            value: data.phone,
                            is_primary: true,
                          },
                        ]
                      : []),
                  ],
                },
              };
              const res = await client.post(
                route('leads.store', { ignore_duplicates: 1 }),
                payload,
              );
              const lead = res.data;
              selectLead({
                id: lead.id,
                business_name: lead.business_name,
                url: route('leads.show', { lead: lead.id }),
              });
              return `Lead "${lead.business_name}" created`;
            }}
            FormComponent={LeadForm}
            buttonLabel="Quick Create"
          />
        </Section>

        <Section
          name="Quote Config"
          description="Updates via real leads.update endpoint"
          disabled={!activeLead}
        >
          <ResourceAction
            defaults={defaults.quote_config}
            onSubmit={async (data) => {
              await client.put(
                route('leads.update', { lead: activeLead!.id }),
                data,
              );
              return 'Quote config applied';
            }}
            FormComponent={QuoteConfigForm}
            buttonLabel="Quick Apply"
          />
        </Section>

        <Section
          name="Lead Rate"
          description="Creates via real leads.rates.store endpoint"
          disabled={!activeLead}
        >
          <ResourceAction
            defaults={defaults.lead_rate}
            onSubmit={async (data) => {
              await client.post(
                route('leads.rates.store', { lead: activeLead!.id }),
                data,
              );
              return `Rate ${data.code} added`;
            }}
            FormComponent={LeadRateForm}
            buttonLabel="Quick Add Rate"
          />
        </Section>

        <Section
          name="Payroll Settings"
          description="Updates via real leads.payroll-settings.update endpoint"
          disabled={!activeLead}
        >
          <ResourceAction
            defaults={defaults.payroll_settings}
            onSubmit={async (data) => {
              await client.put(
                route('leads.payroll-settings.update', {
                  lead: activeLead!.id,
                }),
                data,
              );
              return 'Payroll settings applied';
            }}
            FormComponent={PayrollSettingsForm}
            buttonLabel="Quick Apply"
          />
        </Section>

        <Section
          name="Quote"
          description="Creates via real leads.quotes.store endpoint"
          disabled={!activeLead}
        >
          <ResourceAction
            defaults={defaults.quote}
            onSubmit={async (data) => {
              const res = await client.post(
                route('leads.quotes.store', { lead: activeLead!.id }),
                {
                  type: data.type,
                  theme: data.theme,
                  cutoff: data.cutoff,
                  pay_cycle: data.pay_cycle,
                },
              );
              const quote = res.data;
              await client.put(
                route('leads.quotes.select', {
                  lead: activeLead!.id,
                  lead_quote: quote.id,
                }),
              );
              setActiveQuoteId(quote.id);
              return `Quote #${quote.id} created and selected`;
            }}
            FormComponent={QuoteForm}
            buttonLabel="Quick Create Quote"
          />
        </Section>

        <Section
          name="Submission"
          description="Submits quote to underwriting via real leads.quotes.submit endpoint"
          disabled={!activeLead || !activeQuoteId}
        >
          <SubmissionAction
            leadId={activeLead?.id ?? null}
            quoteId={activeQuoteId}
            onSubmissionCreated={setActiveSubmissionId}
          />
        </Section>

        <Section
          name="Approval"
          description="Approves or rejects submission via real underwriting endpoints"
          disabled={!activeSubmissionId}
        >
          <ApprovalAction submissionId={activeSubmissionId} />
        </Section>
      </Accordion>
    </VStack>
  );
}

registerDevTool({
  name: 'Resources',
  component: ResourceComposerV2,
});
